import React, { useCallback, useRef } from "react";
import { useFormikContext } from "formik";
import { FieldLabel, TextInput, FieldHelperText, Link } from "@td/ui";
import { translate } from "@td/shared_utils";
import { useConsultationSearch } from "../hooks";
import { fieldTypes, defaultTaskFormValues } from "../constants";

const ConsultField = React.memo(() => {
  const textInputRef = useRef();
  const { values, getFieldMeta, setFieldValue, setFieldError, setFieldTouched } = useFormikContext();
  const fieldMeta = getFieldMeta(fieldTypes.CONSULT_ID);
  const { findConsultation } = useConsultationSearch();

  const consultId = values[fieldTypes.CONSULT_ID].value;
  const member = values[fieldTypes.MEMBER].value || {};
  const isRestricted = member.isRestrictedForAdmin || false;
  const isReadOnly = values[fieldTypes.NOT_CONSULT_RELATED];

  const clearFields = useCallback(() => {
    setFieldValue(fieldTypes.CONSULT_ID, defaultTaskFormValues[fieldTypes.CONSULT_ID], false);
    setFieldValue(fieldTypes.MEMBER, defaultTaskFormValues[fieldTypes.MEMBER], false);
    setFieldValue(fieldTypes.GROUP, defaultTaskFormValues[fieldTypes.GROUP], false);
    setFieldValue(fieldTypes.PROVIDER, defaultTaskFormValues[fieldTypes.PROVIDER], false);
  }, [setFieldValue]);

  const handleKeyDown = useCallback(async (e) => {
    if (isReadOnly) {
      return;
    }

    setFieldTouched(fieldTypes.CONSULT_ID, true, false);

    if (e.key === "Enter") {
      e.preventDefault();

      if (e.target.value) {
        const consultation = await findConsultation(e.target.value);

        if (consultation) {
          const { id, memberProfile: member, provider } = consultation;
          const { group } = member;

          setFieldValue(`${fieldTypes.CONSULT_ID}.value`, id);
          setFieldValue(fieldTypes.MEMBER, {
            value: member,
            label: translate(null, "care_team_tasks.form.fields.member.option", "label", {
              id: member.id,
              name: member.name,
            }),
          });
          setFieldValue(fieldTypes.GROUP, group.name);
          setFieldValue(fieldTypes.PROVIDER,
            provider
              ? translate(null, "care_team_tasks.form.fields.provider.option", "label", {
                  id: provider.id,
                  name: provider.name,
                })
              : ""
          );

          textInputRef.current.blur();
        } else {
          clearFields();
          setFieldError(
            fieldTypes.CONSULT_ID,
            translate(null, "care_team_tasks.form.fields.consult.errors", "not_found")
          );
        }
      } else {
        clearFields();
        setFieldError(
          fieldTypes.CONSULT_ID,
          translate(null, "care_team_tasks.form.fields.consult.errors", "required")
        );
      }
    }
  }, [textInputRef.current, findConsultation, clearFields, setFieldValue, setFieldError, setFieldTouched, isReadOnly]);

  const handleChange = useCallback((e) => {
    if (/\D/.test(e.target.value)) {
      setFieldValue(`${fieldTypes.CONSULT_ID}.label`, e.target.value.replace(/\D/g, ""), false);
      setFieldError(
        fieldTypes.CONSULT_ID,
        translate(null, "care_team_tasks.form.fields.consult.errors", "only_digits")
      );
    } else {
      setFieldValue(`${fieldTypes.CONSULT_ID}.label`, e.target.value, false);
      setFieldError(fieldTypes.CONSULT_ID, null);
    }
  }, [setFieldValue, setFieldError]);

  const handleBlur = useCallback(() => {
    if (isReadOnly) {
      return;
    }

    setFieldValue(`${fieldTypes.CONSULT_ID}.label`, values[fieldTypes.CONSULT_ID].value || "", false);
  }, [values[fieldTypes.CONSULT_ID], isReadOnly]);

  const fieldLabel = `${translate(null, "care_team_tasks.form.fields.consult", "label")} ${!isReadOnly ? "*" : ""}`.trim();

  return (
    <FieldLabel>
      {consultId ? (
        <Link
          target="_blank"
          restricted={isRestricted}
          href={window.Routes.practice_management_frontend_consultation_path(consultId)}
        >
          {fieldLabel}
        </Link>
      ) : (
        <React.Fragment>
          {fieldLabel}
        </React.Fragment>
      )}

      <TextInput
        ref={textInputRef}
        autoComplete="off"
        readOnly={isReadOnly}
        value={values[fieldTypes.CONSULT_ID].label || ""}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={!isReadOnly ? translate(null, "care_team_tasks.form.fields.consult", "placeholder") : ""}
        invalid={fieldMeta.error && fieldMeta.touched}
      />

      <FieldHelperText error={fieldMeta.error && fieldMeta.touched}>
        {fieldMeta.error}
      </FieldHelperText>
    </FieldLabel>
  );
});

export default ConsultField;
